import { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Camera, Upload, Spinner, X, Plus, Trash, CheckCircle, UploadSimple } from "@phosphor-icons/react";
import { verdictAPI } from "@/lib/api";
import { processImageFile } from "@/lib/image-utils";
import VerdictCard from "@/components/app/VerdictCard";
import AddToHaulModal from "@/components/app/AddToHaulModal";
import BottomNav from "@/components/app/BottomNav";

export default function ScanPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showWelcome, setShowWelcome] = useState(searchParams.get("welcome") === "1");
  const [ebayStatus, setEbayStatus] = useState(searchParams.get("ebay") || "");
  const [photos, setPhotos] = useState([]);
  const [verdict, setVerdict] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showHaulModal, setShowHaulModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processProgress, setProcessProgress] = useState({ done: 0, total: 0 });
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    if (ebayStatus) {
      const timer = setTimeout(() => {
        setEbayStatus("");
        searchParams.delete("ebay");
        searchParams.delete("reason");
        setSearchParams(searchParams, { replace: true });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [ebayStatus]);

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);
    setProcessing(true);
    setProcessProgress({ done: 0, total: fileList.length });
    for (let i = 0; i < fileList.length; i++) {
      const result = await processImageFile(fileList[i]);
      setPhotos((prev) => [...prev, result]);
      setProcessProgress({ done: i + 1, total: fileList.length });
    }
    setVerdict(null);
    setError("");
    setProcessing(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const files = e.dataTransfer?.files;
    if (files?.length) handleFiles(files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const removePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAnalyze = async () => {
    if (photos.length === 0) return;
    setLoading(true);
    setError("");
    try {
      // Send all photos combined - use the first as primary, note the count
      // For multi-photo, we'll concatenate base64 data with a separator the backend can handle
      const { data } = await verdictAPI.analyze(
        photos.map((p) => p.base64),
      );
      setVerdict(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Analysis failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPhotos([]);
    setVerdict(null);
    setError("");
  };

  const hasPhotos = photos.length > 0;

  return (
    <div className="min-h-screen bg-white pb-32">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b-4 border-black py-4 px-6 flex justify-between items-center">
        <h1 className="font-heading text-2xl font-black tracking-tighter uppercase">
          SCAN
        </h1>
        {(hasPhotos || verdict) && (
          <button
            onClick={handleReset}
            className="border-2 border-black p-2 active:translate-y-[1px] transition-transform"
            data-testid="scan-reset-btn"
          >
            <X size={20} weight="bold" />
          </button>
        )}
      </div>

      <div className="px-4 py-6 space-y-6">
        {/* Welcome banner for new signups */}
        {showWelcome && (
          <div
            className="border-4 border-black bg-[#00E676] p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex items-start gap-3"
            data-testid="welcome-banner"
          >
            <CheckCircle size={28} weight="bold" className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-heading text-xl font-black tracking-tight uppercase">
                You're in!
              </div>
              <p className="font-body text-sm mt-1">
                Upload or snap a pic of an item to get started.
              </p>
            </div>
            <button
              onClick={() => { setShowWelcome(false); setSearchParams({}); }}
              className="shrink-0 p-1"
              data-testid="dismiss-welcome"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
        )}

        {/* eBay connection status banner */}
        {ebayStatus && (
          <div
            className={`border-4 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex items-center gap-3 ${
              ebayStatus === "connected" ? "bg-[#00E676]" : "bg-[#FF5252]"
            }`}
            data-testid="ebay-status-banner"
          >
            <CheckCircle size={24} weight="bold" className="shrink-0" />
            <span className="font-heading text-sm font-bold uppercase flex-1">
              {ebayStatus === "connected"
                ? "eBay account connected!"
                : ebayStatus === "declined"
                ? "eBay authorization declined"
                : "eBay connection failed"}
            </span>
            <button onClick={() => setEbayStatus("")} className="shrink-0 p-1">
              <X size={16} weight="bold" />
            </button>
          </div>
        )}

        {/* Upload progress bar */}
        {processing && (
          <div className="border-4 border-black bg-white p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="upload-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="font-heading text-sm font-bold uppercase">
                Uploading {processProgress.total === 1 ? "photo" : "photos"}...
              </span>
              <span className="font-body text-sm font-semibold">
                {processProgress.done}/{processProgress.total}
              </span>
            </div>
            <div className="w-full h-4 border-2 border-black bg-neutral-100 overflow-hidden">
              <div
                className="h-full bg-[#002FA7] transition-all duration-300"
                style={{ width: `${processProgress.total > 0 ? (processProgress.done / processProgress.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Upload Area - shown when no photos and no verdict */}
        {!hasPhotos && !verdict && (
          <div className="space-y-4" data-testid="scan-upload-area">
            <div
              className={`border-4 border-dashed p-8 flex flex-col items-center justify-center min-h-[300px] gap-6 transition-colors ${
                dragging ? "border-[#002FA7] bg-blue-50" : "border-black"
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              data-testid="scan-drop-zone"
            >
              <div className="font-heading text-xl font-bold tracking-tight text-center uppercase">
                {dragging ? "Drop Photos Here" : "Snap, Upload, or Drag & Drop"}
              </div>
              <p className="font-body text-sm text-neutral-500 text-center">
                {dragging ? "Release to add photos" : "Take a photo or drop images of a thrift item"}
              </p>

              <div className="flex gap-4 w-full max-w-xs">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex-1 bg-[#002FA7] text-white p-4 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[0px_0px_0px_0px_rgba(0,0,0,1)] transition-all flex items-center justify-center gap-2 font-heading font-bold uppercase tracking-wide"
                  data-testid="scan-camera-btn"
                >
                  <Camera size={24} weight="bold" />
                  Photo
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 bg-white text-black p-4 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[0px_0px_0px_0px_rgba(0,0,0,1)] transition-all flex items-center justify-center gap-2 font-heading font-bold uppercase tracking-wide"
                  data-testid="scan-upload-btn"
                >
                  <Upload size={24} weight="bold" />
                  Upload
                </button>
              </div>
            </div>

            {/* Quick-list Link — fast single-item upload + AI listing flow */}
            <Link
              to="/quick-list"
              className="block w-full bg-white text-black py-3 border-2 border-black font-heading text-sm font-bold uppercase tracking-wide text-center flex items-center justify-center gap-2 active:bg-neutral-50 shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] active:translate-y-[1px] active:translate-x-[1px] active:shadow-none transition-all"
              data-testid="quicklist-link"
            >
              <UploadSimple size={18} weight="bold" /> Quick List · Skip the Scan
            </Link>

            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = "";
              }}
              data-testid="scan-camera-input"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
              multiple
              className="hidden"
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = "";
              }}
              data-testid="scan-file-input"
            />
          </div>
        )}

        {/* Photo Preview Grid - shown when photos exist but no verdict yet */}
        {hasPhotos && !verdict && (
          <div
            className="space-y-4"
            data-testid="scan-preview-area"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {dragging && (
              <div className="border-4 border-dashed border-[#002FA7] bg-blue-50 p-6 text-center font-heading font-bold uppercase text-[#002FA7]">
                Drop to add more photos
              </div>
            )}
            {/* Photo Grid */}
            <div className={`grid gap-3 ${photos.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
              {photos.map((photo, index) => (
                <div
                  key={index}
                  className="relative border-4 border-black overflow-hidden shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                  data-testid={`scan-photo-${index}`}
                >
                  {photo.preview ? (
                    <img
                      src={photo.preview}
                      alt={`Item photo ${index + 1}`}
                      className={`w-full bg-neutral-100 ${photos.length === 1 ? "max-h-[400px] object-contain" : "aspect-square object-contain"}`}
                    />
                  ) : (
                    <div className={`w-full bg-neutral-100 flex items-center justify-center border border-dashed border-neutral-300 ${photos.length === 1 ? "h-[200px]" : "h-40"}`}>
                      <span className="font-body text-xs text-neutral-500">Photo ready</span>
                    </div>
                  )}
                  <button
                    onClick={() => removePhoto(index)}
                    className="absolute top-2 right-2 bg-[#FF1744] text-white p-1.5 border-2 border-black"
                    data-testid={`remove-photo-${index}`}
                  >
                    <Trash size={16} weight="bold" />
                  </button>
                  <div className="absolute bottom-0 left-0 bg-black text-white px-2 py-1 font-body text-xs font-semibold">
                    {index + 1}/{photos.length}
                  </div>
                </div>
              ))}
            </div>

            {/* Photo Count */}
            <div className="font-body text-sm text-neutral-500 text-center">
              {photos.length} photo{photos.length !== 1 ? "s" : ""} added
            </div>

            {error && (
              <div
                className="border-2 border-black bg-[#FF1744] text-white p-3 font-body text-sm font-medium"
                data-testid="scan-error"
              >
                {error}
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Take Another Photo */}
              <div className="flex gap-3">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex-1 bg-white text-black p-4 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[0px_0px_0px_0px_rgba(0,0,0,1)] transition-all flex items-center justify-center gap-2 font-heading font-bold uppercase tracking-wide"
                  data-testid="scan-add-photo-btn"
                >
                  <Camera size={20} weight="bold" />
                  Take Another
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 bg-white text-black p-4 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[0px_0px_0px_0px_rgba(0,0,0,1)] transition-all flex items-center justify-center gap-2 font-heading font-bold uppercase tracking-wide"
                  data-testid="scan-add-upload-btn"
                >
                  <Plus size={20} weight="bold" />
                  Upload More
                </button>
              </div>

              {/* Get Verdict */}
              <button
                onClick={handleAnalyze}
                disabled={loading}
                className="w-full bg-[#00E676] text-black p-5 font-heading font-black text-xl uppercase tracking-wide border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[0px_0px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                data-testid="scan-analyze-btn"
              >
                {loading ? (
                  <>
                    <Spinner size={28} weight="bold" className="animate-spin" />
                    Analyzing {photos.length} photo{photos.length !== 1 ? "s" : ""}...
                  </>
                ) : (
                  `Get Verdict`
                )}
              </button>
            </div>

            {/* Hidden file inputs (reused) */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = "";
              }}
              data-testid="scan-camera-input"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
              multiple
              className="hidden"
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = "";
              }}
              data-testid="scan-file-input"
            />
          </div>
        )}

        {/* Verdict Result */}
        {verdict && (
          <div data-testid="scan-verdict-result">
            <VerdictCard
              verdict={verdict}
            />
          </div>
        )}
      </div>

      {/* Add to Haul Modal */}
      {showHaulModal && verdict && (
        <AddToHaulModal
          verdict={verdict}
          onClose={() => setShowHaulModal(false)}
        />
      )}

      <BottomNav />
    </div>
  );
}
